(() => {
  'use strict';

  const existing = window.__yrPosterUploadBoot;
  if (existing) {
    existing.scan();
    return;
  }

  const MAX_PHOTOS = 3;
  const MAX_SOURCE_BYTES = 8 * 1024 * 1024;
  const MAX_EDGE = 1536;
  const POLL_INTERVAL_MS = 2500;
  const POLL_TIMEOUT_MS = 120000;
  const instances = new Set();
  let photoSequence = 0;
  let requestSequence = 0;
  let scanQueued = false;

  function sessionId() {
    try {
      const key = 'yr_preview_sid';
      let value = window.localStorage.getItem(key);
      if (!value) {
        value = window.crypto?.randomUUID?.() || `yr${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
        window.localStorage.setItem(key, value);
      }
      return value;
    } catch (_) {
      return '';
    }
  }

  async function responseJson(response) {
    if (response.ok) return response.json();

    const data = await response.json().catch(() => ({}));
    let message = data?.message || data?.error;
    if (!message) {
      if (response.status === 413) message = 'That photo is too large. Use one under 8 MB.';
      else if (response.status === 415) message = 'Use a JPEG or PNG photo. If your photo is HEIC, convert or share it as JPEG first.';
      else if (response.status === 429) message = 'Today’s preview limit has been reached. Please try again tomorrow.';
      else if (response.status === 404) message = 'That upload expired. Please add the photos again.';
      else message = 'The portrait preview is unavailable right now. Please try again later.';
    }

    const error = new Error(message);
    error.userMessage = message;
    throw error;
  }

  async function fetchJson(url, options = {}, timeoutMs = 30000) {
    const controller = new AbortController();
    const { signal: externalSignal, ...fetchOptions } = options;
    let timedOut = false;
    const abortFromOutside = () => controller.abort();

    if (externalSignal?.aborted) controller.abort();
    else externalSignal?.addEventListener('abort', abortFromOutside, { once: true });

    const timeout = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);

    try {
      const response = await fetch(url, { ...fetchOptions, signal: controller.signal });
      return await responseJson(response);
    } catch (problem) {
      if (problem?.name === 'AbortError' && timedOut) {
        const timeoutError = new Error('The request took too long. Check your connection and try again.');
        timeoutError.userMessage = timeoutError.message;
        throw timeoutError;
      }
      throw problem;
    } finally {
      window.clearTimeout(timeout);
      externalSignal?.removeEventListener('abort', abortFromOutside);
    }
  }

  function fileKind(file) {
    const type = String(file?.type || '').toLowerCase();
    const extension = String(file?.name || '').split('.').pop().toLowerCase();
    if (type === 'image/jpeg' || extension === 'jpg' || extension === 'jpeg') return 'jpeg';
    if (type === 'image/png' || extension === 'png') return 'png';
    if (type === 'image/heic' || type === 'image/heif' || extension === 'heic' || extension === 'heif') return 'heic';
    return '';
  }

  function loadImage(file, timeoutMs = 12000) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const image = new Image();
      let settled = false;
      const timeout = window.setTimeout(() => finish(new Error('Image decoding timed out.')), timeoutMs);

      function finish(problem) {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        image.onload = null;
        image.onerror = null;
        if (problem) {
          URL.revokeObjectURL(url);
          reject(problem);
        } else {
          resolve({ image, url });
        }
      }

      image.onload = () => {
        if (!image.naturalWidth || !image.naturalHeight) finish(new Error('The image has no readable pixels.'));
        else finish();
      };
      image.onerror = () => finish(new Error('This browser could not decode the image.'));
      image.src = url;
    });
  }

  async function canDecode(file) {
    const loaded = await loadImage(file);
    URL.revokeObjectURL(loaded.url);
    return true;
  }

  async function shrinkImage(file) {
    const loaded = await loadImage(file);
    try {
      const width = loaded.image.naturalWidth;
      const height = loaded.image.naturalHeight;
      const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(width * scale));
      canvas.height = Math.max(1, Math.round(height * scale));
      const context = canvas.getContext('2d');
      if (!context) throw new Error('This browser cannot prepare the photo.');
      context.drawImage(loaded.image, 0, 0, canvas.width, canvas.height);
      return await new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('This browser cannot prepare the photo.'));
        }, 'image/jpeg', 0.9);
      });
    } finally {
      URL.revokeObjectURL(loaded.url);
    }
  }

  function init(root) {
    if (!root || root.dataset.yrPosterReady === 'true') return null;
    root.dataset.yrPosterReady = 'true';

    const query = (selector) => root.querySelector(selector);
    const productId = String(root.dataset.productId || '');
    const formId = String(root.dataset.formId || '');
    const mainProduct = root.closest('product-info') || document.getElementById(`MainProduct-${formId.replace('product-form-', '')}`);
    const variants = new Map();

    try {
      const data = JSON.parse(query('[data-yr-variants]')?.textContent || '[]');
      data.forEach((variant) => variants.set(String(variant.id), Boolean(variant.available)));
    } catch (_) {
      // An unknown variant is kept unavailable rather than incorrectly enabling checkout.
    }

    const drop = query('[data-yr-drop]');
    const libraryInput = query('[data-yr-library]');
    const cameraInput = query('[data-yr-camera]');
    const libraryButton = query('[data-yr-library-button]');
    const cameraButton = query('[data-yr-camera-button]');
    const photoActions = query('[data-yr-photo-actions]');
    const thumbnails = query('[data-yr-thumbnails]');
    const photoCount = query('[data-yr-photo-count]');
    const createButton = query('[data-yr-create]');
    const progress = query('[data-yr-progress]');
    const progressbar = query('[data-yr-progressbar]');
    const progressFill = query('[data-yr-progress-fill]');
    const status = query('[data-yr-status]');
    const result = query('[data-yr-result]');
    const previewImage = query('[data-yr-image]');
    const approveButton = query('[data-yr-approve]');
    const error = query('[data-yr-error]');
    const gate = query('[data-yr-gate]');

    if (!drop || !libraryInput || !cameraInput || !libraryButton || !cameraButton || !photoActions || !thumbnails || !photoCount || !createButton || !progress || !progressbar || !progressFill || !status || !result || !previewImage || !approveButton || !error || !gate) {
      delete root.dataset.yrPosterReady;
      return null;
    }

    const state = {
      epoch: 0,
      photoVersion: 0,
      selectionOperation: 0,
      photos: Object.freeze([]),
      activeRequest: null,
      jobId: '',
      photoId: '',
      previewUrl: '',
      ready: false,
      approved: false,
      busy: false,
      selectionBusy: false,
      progressTimer: null,
      pollTimer: null,
      scopeObserver: null,
      gateQueued: false,
    };

    function mainForm() {
      return document.getElementById(formId);
    }

    function stickyForms() {
      return Array.from(document.querySelectorAll('sticky-atc-bar form[action*="/cart/add"]')).filter((form) => {
        const sticky = form.closest('sticky-atc-bar');
        return String(sticky?.dataset.productId || '') === productId;
      });
    }

    function ownedForms() {
      return [mainForm(), ...stickyForms()].filter(Boolean);
    }

    function ownsForm(form) {
      if (!form) return false;
      if (form === mainForm()) return true;
      const sticky = form.closest?.('sticky-atc-bar');
      return Boolean(sticky && String(sticky.dataset.productId || '') === productId);
    }

    function variantAvailable(form) {
      const variant = form?.querySelector('[name="id"]');
      if (!variant || variant.disabled) return false;
      return variants.get(String(variant.value)) === true;
    }

    function setProgress(value) {
      const safe = Math.max(0, Math.min(100, Number(value) || 0));
      progressFill.style.width = `${safe.toFixed(1)}%`;
      progressbar.setAttribute('aria-valuenow', String(Math.round(safe)));
    }

    function stopTimers() {
      window.clearInterval(state.progressTimer);
      window.clearTimeout(state.pollTimer);
      state.progressTimer = null;
      state.pollTimer = null;
    }

    function clearError() {
      error.hidden = true;
      error.textContent = '';
    }

    function showError(message) {
      error.textContent = message;
      error.hidden = false;
    }

    function requestHasExactPhotos(request) {
      return Boolean(
        request &&
        request.photos.length === state.photos.length &&
        request.photos.every((photo, index) => photo === state.photos[index])
      );
    }

    function isCurrent(request) {
      return Boolean(
        request &&
        request === state.activeRequest &&
        request.epoch === state.epoch &&
        request.photoVersion === state.photoVersion &&
        requestHasExactPhotos(request) &&
        root.isConnected
      );
    }

    function refreshCreateButton() {
      createButton.disabled = state.busy || state.selectionBusy || state.photos.length < 1;
    }

    function syncProperties() {
      const validPreview = state.ready && state.activeRequest && isCurrent(state.activeRequest);
      const values = [
        ['[data-yr-job-property]', validPreview ? state.jobId : ''],
        ['[data-yr-photo-property]', validPreview ? state.photoId : ''],
        ['[data-yr-preview-property]', validPreview ? state.previewUrl : ''],
        ['[data-yr-approved-property]', validPreview && state.approved ? 'yes' : ''],
      ];
      values.forEach(([selector, value]) => {
        root.querySelectorAll(selector).forEach((input, index) => {
          const nextValue = index === 0 ? value : '';
          const shouldDisable = index > 0;
          if (input.value !== nextValue) input.value = nextValue;
          if (input.disabled !== shouldDisable) input.disabled = shouldDisable;
        });
      });
    }

    function syncGate() {
      state.gateQueued = false;
      if (!root.isConnected) return;
      const open = Boolean(state.approved && state.ready && state.jobId && state.photoId && state.previewUrl && isCurrent(state.activeRequest));
      ownedForms().forEach((form) => {
        form.querySelectorAll('[name="add"]').forEach((button) => {
          const canSubmit = open && variantAvailable(form);
          const disabled = !canSubmit;
          const gateState = canSubmit ? 'open' : 'closed';
          if (button.disabled !== disabled) button.disabled = disabled;
          // Sleek treats the attribute's presence as a submission lock.
          // Keep its native lock while an add-to-cart request is in flight.
          if (!canSubmit) {
            if (button.getAttribute('aria-disabled') !== 'true') button.setAttribute('aria-disabled', 'true');
          } else if (!button.classList.contains('btn--loading') && button.hasAttribute('aria-disabled')) {
            button.removeAttribute('aria-disabled');
          }
          if (button.dataset.yrPortraitGate !== gateState) button.dataset.yrPortraitGate = gateState;
        });
      });
      root.dataset.approved = open ? 'true' : 'false';
      const gateCopy = open
        ? 'Portrait approved. Choose any available size and finish, then add it to your cart.'
        : 'Create and approve this portrait before adding it to your cart.';
      if (gate.textContent !== gateCopy) gate.textContent = gateCopy;
      syncProperties();
    }

    function scheduleGateSync() {
      if (state.gateQueued) return;
      state.gateQueued = true;
      window.requestAnimationFrame(syncGate);
    }

    function resetRemoteState() {
      const request = state.activeRequest;
      state.epoch += 1;
      state.activeRequest = null;
      request?.controller.abort();
      state.jobId = '';
      state.photoId = '';
      state.previewUrl = '';
      state.ready = false;
      state.approved = false;
      state.busy = false;
      stopTimers();
      previewImage.onload = null;
      previewImage.onerror = null;
      previewImage.removeAttribute('src');
      previewImage.removeAttribute('data-request-token');
      result.hidden = true;
      progress.hidden = true;
      approveButton.disabled = false;
      approveButton.textContent = 'Approve this portrait';
      status.textContent = 'Preparing your portrait…';
      setProgress(0);
      refreshCreateButton();
      syncGate();
    }

    function invalidatePhotos() {
      state.photoVersion += 1;
      resetRemoteState();
    }

    function assertCurrent(request) {
      if (isCurrent(request)) return;
      const stale = new Error('Stale portrait request');
      stale.stale = true;
      throw stale;
    }

    function recover(problem, request) {
      if (!isCurrent(request) || problem?.stale) return;
      const message = problem?.userMessage || 'Something went wrong while creating the portrait. Check your connection and try again.';
      resetRemoteState();
      showError(message);
    }

    function startProgress() {
      let percentage = 0;
      setProgress(percentage);
      state.progressTimer = window.setInterval(() => {
        if (percentage >= 92) return;
        percentage += Math.max(1, (92 - percentage) / 20);
        setProgress(percentage);
      }, 600);
    }

    function imageTypeError(kind) {
      if (kind === 'heic') {
        return 'This browser cannot open that HEIC photo. On iPhone, choose “Most Compatible” in Camera settings, or share/save the photo as JPEG or PNG and try again.';
      }
      return 'We could not open that image. Choose a valid JPEG or PNG photo and try again.';
    }

    function photoName(file, fallbackIndex) {
      const name = String(file?.name || '').trim();
      return name || `Photo ${fallbackIndex + 1}`;
    }

    function renderPhotos() {
      const fragment = document.createDocumentFragment();
      state.photos.forEach((photo, index) => {
        const item = document.createElement('li');
        item.className = 'yr-poster__photo';
        item.dataset.photoId = photo.id;

        const imageWrap = document.createElement('div');
        imageWrap.className = 'yr-poster__thumb-wrap';
        const image = document.createElement('img');
        image.className = 'yr-poster__thumb';
        image.src = photo.url;
        image.alt = index === 0 ? `Selected child photo ${index + 1}, main photo` : `Selected child photo ${index + 1}`;
        image.decoding = 'async';
        imageWrap.append(image);

        if (index === 0) {
          const label = document.createElement('span');
          label.className = 'yr-poster__main-label';
          label.textContent = 'Main photo';
          imageWrap.append(label);
        }

        const buttons = document.createElement('div');
        buttons.className = 'yr-poster__photo-buttons';
        if (index > 0) {
          const mainButton = document.createElement('button');
          mainButton.type = 'button';
          mainButton.className = 'yr-poster__photo-button';
          mainButton.dataset.yrMakePrimary = photo.id;
          mainButton.textContent = 'Use as main photo';
          mainButton.setAttribute('aria-label', `Use photo ${index + 1} as main photo`);
          buttons.append(mainButton);
        }

        const removeButton = document.createElement('button');
        removeButton.type = 'button';
        removeButton.className = 'yr-poster__photo-button yr-poster__photo-button--remove';
        removeButton.dataset.yrRemovePhoto = photo.id;
        removeButton.textContent = 'Remove';
        removeButton.setAttribute('aria-label', `Remove photo ${index + 1}${index === 0 ? ', main photo' : ''}`);
        buttons.append(removeButton);

        item.append(imageWrap, buttons);
        fragment.append(item);
      });

      thumbnails.replaceChildren(fragment);
      const count = state.photos.length;
      thumbnails.hidden = count === 0;
      photoCount.hidden = count === 0;
      photoCount.textContent = count === 0
        ? ''
        : `${count} of ${MAX_PHOTOS} photos selected. The first photo is the main photo.`;
      drop.hidden = count > 0;
      photoActions.hidden = count >= MAX_PHOTOS;
      libraryButton.disabled = state.selectionBusy || count >= MAX_PHOTOS;
      cameraButton.disabled = state.selectionBusy || count >= MAX_PHOTOS;
      refreshCreateButton();
    }

    function replacePhotos(nextPhotos) {
      state.selectionOperation += 1;
      state.selectionBusy = false;
      drop.removeAttribute('aria-busy');
      invalidatePhotos();
      state.photos = Object.freeze(nextPhotos.slice(0, MAX_PHOTOS));
      clearError();
      renderPhotos();
    }

    function removePhoto(id) {
      const photo = state.photos.find((candidate) => candidate.id === id);
      if (!photo) return;
      URL.revokeObjectURL(photo.url);
      replacePhotos(state.photos.filter((candidate) => candidate !== photo));
    }

    function makePrimary(id) {
      const index = state.photos.findIndex((photo) => photo.id === id);
      if (index <= 0) return;
      const selected = state.photos[index];
      replacePhotos([selected, ...state.photos.slice(0, index), ...state.photos.slice(index + 1)]);
    }

    async function addFiles(fileList) {
      const incoming = Array.from(fileList || []);
      if (!incoming.length || state.photos.length >= MAX_PHOTOS) return;

      const operation = ++state.selectionOperation;
      invalidatePhotos();
      clearError();
      state.selectionBusy = true;
      drop.setAttribute('aria-busy', 'true');
      renderPhotos();

      const basePhotos = state.photos.slice();
      const baseVersion = state.photoVersion;
      const availableSlots = MAX_PHOTOS - basePhotos.length;
      const candidates = incoming.slice(0, availableSlots);
      const accepted = [];
      const problems = [];

      if (incoming.length > availableSlots) {
        problems.push(`Only ${availableSlots} more photo${availableSlots === 1 ? '' : 's'} can be added (3 maximum).`);
      }

      for (let index = 0; index < candidates.length; index += 1) {
        const file = candidates[index];
        const label = photoName(file, basePhotos.length + index);
        const kind = fileKind(file);
        if (!kind) {
          problems.push(`${label}: choose a JPEG, PNG, or HEIC photo.`);
          continue;
        }
        if (file.size > MAX_SOURCE_BYTES) {
          problems.push(`${label}: choose a photo under 8 MB.`);
          continue;
        }

        try {
          await canDecode(file);
          if (!root.isConnected || operation !== state.selectionOperation || baseVersion !== state.photoVersion) break;
          accepted.push(Object.freeze({
            id: `yr-photo-${Date.now().toString(36)}-${++photoSequence}`,
            file,
            url: URL.createObjectURL(file),
          }));
        } catch (_) {
          if (!root.isConnected || operation !== state.selectionOperation || baseVersion !== state.photoVersion) break;
          problems.push(`${label}: ${imageTypeError(kind)}`);
        }
      }

      const stillCurrent = Boolean(
        root.isConnected &&
        operation === state.selectionOperation &&
        baseVersion === state.photoVersion &&
        basePhotos.length === state.photos.length &&
        basePhotos.every((photo, index) => photo === state.photos[index])
      );

      if (!stillCurrent) {
        accepted.forEach((photo) => URL.revokeObjectURL(photo.url));
        return;
      }

      state.photos = Object.freeze([...basePhotos, ...accepted]);
      state.selectionBusy = false;
      drop.removeAttribute('aria-busy');
      renderPhotos();
      if (problems.length) showError(problems.join(' '));
    }

    function renderPreview(url, request, jobId, photoId) {
      assertCurrent(request);
      if (!url) throw new Error('The preview image is missing. Please try again.');

      stopTimers();
      state.busy = true;
      state.ready = false;
      state.approved = false;
      state.jobId = jobId;
      state.photoId = photoId;
      state.previewUrl = url;
      status.textContent = 'Loading your portrait…';
      setProgress(100);
      syncGate();

      const absoluteUrl = new URL(url, document.baseURI).href;
      previewImage.dataset.requestToken = request.token;
      previewImage.onload = () => {
        if (!isCurrent(request) || previewImage.dataset.requestToken !== request.token || previewImage.src !== absoluteUrl) return;
        previewImage.onload = null;
        previewImage.onerror = null;
        state.ready = true;
        state.busy = false;
        progress.hidden = true;
        result.hidden = false;
        approveButton.disabled = false;
        approveButton.textContent = 'Approve this portrait';
        refreshCreateButton();
        syncGate();
      };
      previewImage.onerror = () => {
        if (!isCurrent(request) || previewImage.dataset.requestToken !== request.token) return;
        const problem = new Error('The preview image could not be loaded. Check your connection and try again.');
        problem.userMessage = problem.message;
        recover(problem, request);
      };
      previewImage.src = absoluteUrl;
    }

    async function poll(request, jobId, photoId) {
      if (!isCurrent(request)) return;
      if (Date.now() >= request.deadline) {
        const timeout = new Error('The portrait is taking longer than expected. Please try again.');
        timeout.userMessage = timeout.message;
        recover(timeout, request);
        return;
      }

      try {
        assertCurrent(request);
        const data = await fetchJson(`${root.dataset.job}${encodeURIComponent(jobId)}`, {
          signal: request.controller.signal,
        }, 20000);
        assertCurrent(request);

        if (data.status === 'error') {
          const jobError = new Error(data.error || 'Something went wrong. Try another photo.');
          jobError.userMessage = jobError.message;
          recover(jobError, request);
          return;
        }

        if (data.status === 'done') {
          setProgress(100);
          renderPreview(data.preview_url, request, jobId, photoId);
          return;
        }

        state.pollTimer = window.setTimeout(() => poll(request, jobId, photoId), POLL_INTERVAL_MS);
      } catch (problem) {
        if (!problem?.stale) recover(problem, request);
      }
    }

    async function createPreview() {
      if (state.photos.length < 1) {
        showError('Choose at least one photo first.');
        return;
      }
      if (state.selectionBusy) {
        showError('Wait for your photos to finish loading.');
        return;
      }

      const photos = Object.freeze(state.photos.slice());
      resetRemoteState();
      clearError();
      const request = Object.freeze({
        token: `yr-${Date.now().toString(36)}-${++requestSequence}`,
        epoch: state.epoch,
        photoVersion: state.photoVersion,
        photos,
        sessionId: sessionId(),
        controller: new AbortController(),
        deadline: Date.now() + POLL_TIMEOUT_MS,
      });
      state.activeRequest = request;
      state.busy = true;
      refreshCreateButton();
      progress.hidden = false;
      status.textContent = photos.length === 1 ? 'Preparing your photo…' : `Preparing photo 1 of ${photos.length}…`;
      startProgress();
      syncGate();

      try {
        const photoIds = [];
        for (let index = 0; index < request.photos.length; index += 1) {
          assertCurrent(request);
          const number = index + 1;
          status.textContent = request.photos.length === 1
            ? 'Preparing your photo…'
            : `Preparing photo ${number} of ${request.photos.length}…`;
          const blob = await shrinkImage(request.photos[index].file);
          assertCurrent(request);

          const body = new FormData();
          body.append('session_id', request.sessionId);
          body.append('photo', blob, `photo-${number}.jpg`);
          status.textContent = request.photos.length === 1
            ? 'Uploading your photo…'
            : `Uploading photo ${number} of ${request.photos.length}…`;
          const upload = await fetchJson(root.dataset.upload, {
            method: 'POST',
            body,
            signal: request.controller.signal,
          }, 45000);
          assertCurrent(request);
          const photoId = String(upload.photo_id || '');
          if (!photoId) throw new Error(`Upload ${number} did not return a photo ID.`);
          photoIds.push(photoId);
        }

        assertCurrent(request);
        const exactPhotoIds = Object.freeze(photoIds.slice());
        const primaryPhotoId = exactPhotoIds[0];
        const referencePhotoIds = Object.freeze(exactPhotoIds.slice(1, MAX_PHOTOS));
        status.textContent = 'Creating your portrait…';
        const preview = await fetchJson(root.dataset.preview, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            photo_id: primaryPhotoId,
            reference_photo_ids: referencePhotoIds,
            nation: root.dataset.nation || 'Christmas Dragon',
            gender: root.dataset.gender || 'Child',
            session_id: request.sessionId,
          }),
          signal: request.controller.signal,
        }, 30000);
        assertCurrent(request);
        const jobId = String(preview.job_id || '');
        if (!jobId) throw new Error('The preview did not return a job ID.');

        state.photoId = primaryPhotoId;
        state.jobId = jobId;
        status.textContent = 'Creating your portrait…';
        await poll(request, jobId, primaryPhotoId);
      } catch (problem) {
        if (!problem?.stale) recover(problem, request);
      }
    }

    function approvePreview() {
      const request = state.activeRequest;
      if (!request || !isCurrent(request) || !state.ready || !state.previewUrl || !previewImage.complete || !previewImage.naturalWidth) {
        showError('Wait for the private preview to finish loading.');
        return;
      }
      clearError();
      state.approved = true;
      approveButton.disabled = true;
      approveButton.textContent = 'Portrait approved';
      syncGate();
    }

    function openLibrary() {
      if (state.selectionBusy || state.photos.length >= MAX_PHOTOS) return;
      libraryInput.value = '';
      libraryInput.click();
    }

    function openCamera() {
      if (state.selectionBusy || state.photos.length >= MAX_PHOTOS) return;
      cameraInput.value = '';
      cameraInput.click();
    }

    drop.addEventListener('click', openLibrary);
    drop.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        openLibrary();
      }
    });
    drop.addEventListener('dragenter', (event) => {
      event.preventDefault();
      drop.classList.add('is-dragging');
    });
    drop.addEventListener('dragover', (event) => {
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
      drop.classList.add('is-dragging');
    });
    drop.addEventListener('dragleave', (event) => {
      if (!drop.contains(event.relatedTarget)) drop.classList.remove('is-dragging');
    });
    drop.addEventListener('drop', (event) => {
      event.preventDefault();
      drop.classList.remove('is-dragging');
      addFiles(event.dataTransfer?.files);
    });
    libraryButton.addEventListener('click', openLibrary);
    cameraButton.addEventListener('click', openCamera);
    libraryInput.addEventListener('change', () => addFiles(libraryInput.files));
    cameraInput.addEventListener('change', () => addFiles(cameraInput.files));
    thumbnails.addEventListener('click', (event) => {
      const removeButton = event.target.closest?.('[data-yr-remove-photo]');
      if (removeButton) {
        removePhoto(removeButton.dataset.yrRemovePhoto);
        return;
      }
      const primaryButton = event.target.closest?.('[data-yr-make-primary]');
      if (primaryButton) makePrimary(primaryButton.dataset.yrMakePrimary);
    });

    createButton.addEventListener('click', createPreview);
    approveButton.addEventListener('click', approvePreview);

    const scope = mainProduct || document.body;
    scope.addEventListener('change', (event) => {
      if (event.target?.matches?.('[name="id"], variant-selects input, variant-selects select')) {
        window.setTimeout(syncGate, 0);
      }
    });
    state.scopeObserver = new MutationObserver(scheduleGateSync);
    state.scopeObserver.observe(scope, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled', 'value'],
    });

    renderPhotos();
    syncGate();

    return {
      root,
      ownsForm,
      mainForm,
      isApproved: () => Boolean(state.approved && state.ready && isCurrent(state.activeRequest)),
      syncGate,
      blockSubmission() {
        showError('Create and approve this portrait before adding it to your cart.');
        root.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
      destroy() {
        state.selectionOperation += 1;
        resetRemoteState();
        state.scopeObserver?.disconnect();
        state.photos.forEach((photo) => URL.revokeObjectURL(photo.url));
        state.photos = Object.freeze([]);
      },
    };
  }

  function scan() {
    scanQueued = false;
    instances.forEach((instance) => {
      if (!instance.root.isConnected) {
        instance.destroy();
        instances.delete(instance);
      } else instance.syncGate();
    });
    document.querySelectorAll('[data-yr-poster-upload]').forEach((root) => {
      const instance = init(root);
      if (instance) instances.add(instance);
    });
  }

  function scheduleScan() {
    if (scanQueued) return;
    scanQueued = true;
    window.requestAnimationFrame(scan);
  }

  document.addEventListener('submit', (event) => {
    const form = event.target;
    const instance = Array.from(instances).find((candidate) => candidate.root.isConnected && candidate.ownsForm(form));
    if (!instance) return;

    if (!instance.isApproved()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      instance.blockSubmission();
      return;
    }

    const nativeForm = instance.mainForm();
    if (form !== nativeForm) {
      event.preventDefault();
      event.stopImmediatePropagation();
      nativeForm?.requestSubmit();
    }
  }, true);

  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('shopify:section:reorder', scan);
  document.addEventListener('shopify:section:unload', scheduleScan);
  document.addEventListener('change', () => window.setTimeout(() => {
    instances.forEach((instance) => instance.syncGate());
  }, 0));

  const observer = new MutationObserver(scheduleScan);
  observer.observe(document.documentElement, { childList: true, subtree: true });

  window.__yrPosterUploadBoot = { scan };
  scan();
})();